import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import { useProfile } from '../../(identity)/hooks/UserHooks';
import { deleteCreative, getCreatives } from '../services/creativeApi';
import { Creative, CreativeType } from '../types/creative';
import AddEditCreativeModal from '../components/creative/AddEditCreativeModal';
import PreviewCreativeModal from '../components/creative/PreviewCreativeModal';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';

const Index = (props: any) => {
    const { userProfile } = useProfile();
    const [data, setData] = useState<Creative[]>([]);
    const [dataToEdit, setDataToEdit] = useState<Creative | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddEditModalOpen, setIsAddEditModalOpen] = useState<boolean>(false);
    const [isPreviewCreativeModalOpen, setIsPreviewCreativeModalOpen] = useState<boolean>(false);

    useEffect(() => {
        getCreatives().then(setData).finally(() => setLoading(false));
    }, []);

    const handleAddUpdateData = (newData: Creative) => {
        setLoading(true);
        getCreatives().then(setData).finally(() => setLoading(false));
    };

    const toggleAddEditModal = () => {
        setIsAddEditModalOpen(!isAddEditModalOpen);
    };

    const togglePreviewCreativeModal = () => {
        setIsPreviewCreativeModalOpen(!isPreviewCreativeModalOpen);
    };

    const dateFormatOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'shortOffset',
    };

    const columns = [
        {
            header: '#',
            cell: (cell: any) => {
                return (
                    <span className="fw-semibold">{cell.getValue()}</span>
                );
            },
            accessorKey: 'id',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome',
            accessorKey: 'name',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Tipo',
            accessorKey: 'type',
            cell: (cell: any) => {
                const creativeType = cell.getValue();
                const badgeColor = (() => {
                    switch (creativeType) {
                        case CreativeType.IMAGE:
                            return 'info';
                        case CreativeType.HTML5:
                            return 'primary';
                        case CreativeType.VIDEO_VAST:
                            return 'warning';
                        case CreativeType.AUDIO_VAST:
                            return 'success';
                        default:
                            return 'danger';
                    }
                })();
                return (
                    <span className={`badge bg-${badgeColor}`}>
                        {creativeType.split('_')[0] || 'não encontrado'}
                    </span>
                );
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Tamanho',
            accessorKey: 'size',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Pedidos',
            accessorKey: 'orders.length',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Criado em',
            accessorFn: (row: any) => {
                const date = new Date(row.createdAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Atualizado em',
            accessorFn: (row: any) => {
                const date = new Date(row.updatedAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Master Creatives ID',
            accessorFn: (row: any) => {
                const masterCreatives = row.masterCreatives || [];
                return masterCreatives.length > 0
                    ? masterCreatives.map((mc: any) => mc.id).join(', ')
                    : '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Companion Creative ID',
            accessorFn: (row: any) => row.companionAd?.id ?? '-',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                let actionCell = [];

                if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                    actionCell.push(
                        <TooltipProvider key="action_delete">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="btn text-danger d-inline-block"
                                        onClick={() => {
                                            deleteCreative(row).then(() => {
                                                toast.success('Criativo deletado com sucesso');
                                                setLoading(true);
                                                getCreatives().then(setData).finally(() => setLoading(false));
                                            });
                                        }}
                                    >
                                        <i className="ri-delete-bin-2-fill fs-16"></i>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Deletar</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                }

                actionCell.push(
                    <TooltipProvider key="action_preview">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    className="btn text-primary d-inline-block"
                                    onClick={() => {
                                        setDataToEdit(row);
                                        togglePreviewCreativeModal();
                                    }}
                                >
                                    <i className="ri-eye-fill fs-16"></i>
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Preview</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                );
                return <div className="flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
        },
    ];

    document.title = 'Criativos | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="grid gap-4">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <CardTitle>Criativos</CardTitle>
                                    </div>
                                    <div>
                                        {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                            <Button
                                                onClick={() => {
                                                    setDataToEdit(null);
                                                    toggleAddEditModal();
                                                }}
                                                className="bg-green-600 hover:bg-green-700"
                                            >
                                                <i className="ri-add-line align-bottom mr-1"></i> Adicionar criativo
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {!loading ? (
                                    <TableContainer
                                        divClass="table-responsive"
                                        columns={(columns || [])}
                                        data={(data || [])}
                                        isGlobalFilter={true}
                                        customPageSize={30}
                                        SearchPlaceholder="Procurar..."
                                    />
                                ) : (
                                    <div className="flex justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            <AddEditCreativeModal
                creative={dataToEdit}
                isOpen={isAddEditModalOpen}
                toggle={toggleAddEditModal}
                onUpdateData={handleAddUpdateData}
                onAddData={handleAddUpdateData}
            />

            {dataToEdit && (
                <PreviewCreativeModal
                    creative={dataToEdit}
                    isOpen={isPreviewCreativeModalOpen}
                    toggle={togglePreviewCreativeModal}
                />
            )}
        </React.Fragment>
    );
};

export default withRouter(Index);
